import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'

const API_HOST = import.meta.env.VITE_API_URL ?? ''
const BASE_URL = `${API_HOST}/api`
const SETUP_TOKEN_KEY = 'roi365_mfa_setup_token'

interface AcceptResponse {
  mfaSetupRequired: boolean
  setupToken?: string
  email: string
  name: string
}

export function AcceptInvitePage() {
  const navigate = useNavigate()
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token')?.trim() ?? ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE_URL}/auth/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      })
      const json = await res.json()
      if (!res.ok) {
        throw new Error(json?.error?.message ?? 'No se pudo aceptar la invitación')
      }
      return json.data as AcceptResponse
    },
    onSuccess: (data) => {
      if (data.mfaSetupRequired && data.setupToken) {
        sessionStorage.setItem(SETUP_TOKEN_KEY, data.setupToken)
        toast.success('Contraseña establecida. Configura ahora tu MFA.')
        navigate({ to: '/login/mfa-setup' })
        return
      }
      toast.success('Cuenta activada. Inicia sesión con tu nueva contraseña.')
      navigate({ to: '/login' })
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres')
      return
    }
    if (password !== confirm) {
      toast.error('Las contraseñas no coinciden')
      return
    }
    acceptMutation.mutate()
  }

  if (!token) {
    return (
      <div
        className="flex min-h-screen items-center justify-center px-4"
        style={{ backgroundColor: 'var(--color-background)' }}
      >
        <div
          className="w-full max-w-md rounded-xl border p-8 text-center"
          style={{
            backgroundColor: 'var(--color-surface)',
            borderColor: 'var(--color-border)',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          <h1 className="mb-2 text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Enlace inválido
          </h1>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            El enlace de invitación no incluye un token. Pídele al administrador que te genere uno nuevo.
          </p>
          <button
            type="button"
            onClick={() => navigate({ to: '/login' })}
            className="mt-4 text-sm underline"
            style={{ color: 'var(--color-text-link)' }}
          >
            Ir a iniciar sesión
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center px-4"
      style={{ backgroundColor: 'var(--color-background)' }}
    >
      <div
        className="w-full max-w-md rounded-xl border p-8"
        style={{
          backgroundColor: 'var(--color-surface)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <h1 className="mb-1 text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          Activar cuenta
        </h1>
        <p className="mb-6 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          Establece tu contraseña para activar tu acceso a ROI365.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
              Contraseña
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="Mínimo 8 caracteres"
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={{ borderColor: 'var(--color-border)' }}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
              Repetir contraseña
            </label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={{ borderColor: 'var(--color-border)' }}
            />
          </div>
          <button
            type="submit"
            disabled={acceptMutation.isPending}
            className="w-full rounded-md px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
            style={{ backgroundColor: 'var(--color-brand-primary)' }}
          >
            {acceptMutation.isPending ? 'Activando…' : 'Establecer contraseña'}
          </button>
        </form>
      </div>
    </div>
  )
}
